import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NoteModel } from '@seahorse/domain';
import {
  ShButtonComponent,
  ShCheckboxComponent,
  ShFieldComponent,
  ShInputComponent,
  ShTextareaComponent,
} from '@seahorse/components';
import { DIALOG_DATA, DialogContent, DialogRef } from '../dialog/dialog.models';
import { DialogFooterComponent } from '../dialog/dialog-footer.component';

/** What the dialog is opened with: the note to edit, none to add one. */
export interface NoteDialogData {
  note?: NoteModel;
}

/**
 * The body of the add / edit note dialog (`DialogService.open`): description,
 * remarks and the pinned flag. It edits a copy, so cancelling leaves the note
 * in the list untouched.
 */
@Component({
  selector: 'sh-note-form',
  templateUrl: './note-form.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    DialogFooterComponent,
    ShButtonComponent,
    ShCheckboxComponent,
    ShFieldComponent,
    ShInputComponent,
    ShTextareaComponent,
  ],
})
export class NoteFormComponent
  implements DialogContent<NoteDialogData, NoteModel>
{
  readonly data = inject<NoteDialogData>(DIALOG_DATA);
  readonly dialogRef = inject<DialogRef<NoteModel>>(DialogRef);

  readonly note: NoteModel = this.data?.note
    ? { ...this.data.note }
    : NoteModel.default();

  /** Errors show once the user left a field. */
  descriptionTouched = false;
  remarksTouched = false;

  get isDescriptionValid(): boolean {
    return !!this.note.description?.trim();
  }

  get isRemarksValid(): boolean {
    return !!this.note.remarks?.trim();
  }

  get isValid(): boolean {
    return this.isDescriptionValid && this.isRemarksValid;
  }

  ok() {
    if (this.isValid) {
      this.dialogRef.ok(this.note);
    }
  }
}
